import { Machine, MachineConfig, assign } from 'xstate';
import { ModelScreenshot } from 'src/generated/graphql';
import constants from 'src/constants';

const { RENDERING_QUEUED_STATES } = constants.screenshot;

export interface GalleryItemContext {
  enhancedImageUrl?: string | null;
  isRenderingFailed: boolean;
  isRenderingComplete: boolean;
  isRenderingInProgress: boolean;
  screenshot: ModelScreenshot | null;
  isRenderingQueued: boolean;
}

interface GalleryItemSchema {
  context: GalleryItemContext;
  states: {
    unknown: {};
    rendering: {};
    error: {};
    complete: {};
  };
}

interface GalleryItemEvent {
  type: 'UPDATE_STATUS';
  screenshot: ModelScreenshot;
}

export const config: MachineConfig<
  GalleryItemContext,
  GalleryItemSchema,
  GalleryItemEvent
> = {
  initial: 'unknown',
  context: {
    screenshot: null,
    enhancedImageUrl: null,
    isRenderingFailed: false,
    isRenderingComplete: false,
    isRenderingInProgress: false,
    isRenderingQueued: false,
  },
  on: {
    UPDATE_STATUS: 'unknown',
  },
  states: {
    unknown: {
      entry: 'assignModelScreenshot',
      on: {
        '': [
          {
            target: 'complete',
            cond: 'isComplete',
            actions: 'assignRender',
          },
          {
            target: 'error',
            cond: 'isFailed',
            actions: 'assignError',
          },
          {
            target: 'rendering',
            actions: 'assignPlaceholder',
          },
        ],
      },
    },
    rendering: {},
    complete: {},
    error: {},
  },
};

const guards = {
  isComplete: (context: GalleryItemContext) =>
    context.screenshot?.rendering.state === 'completed',
  isFailed: (context: GalleryItemContext) =>
    context.screenshot?.rendering.state === 'failed',
};

const actions = {
  assignModelScreenshot: assign(
    (context: GalleryItemContext, event: GalleryItemEvent) => ({
      screenshot: event.screenshot || context.screenshot,
    }),
  ),
  assignPlaceholder: assign((context: GalleryItemContext) => {
    const state = context.screenshot?.rendering.state!;
    return {
      enhancedImageUrl: context.screenshot?.placeholderUrl,
      isRenderingInProgress: state === 'rendering',
      isRenderingQueued: RENDERING_QUEUED_STATES.includes(state),
    };
  }),
  assignRender: assign((context: GalleryItemContext) => ({
    enhancedImageUrl: context.screenshot!.rendering.versionUrls!.thumbnail,
    isRenderingComplete: true,
    isRenderingInProgress: false,
  })),
  assignError: assign((_: GalleryItemContext) => ({
    isRenderingFailed: true,
    isRenderingInProgress: false,
  })),
};

export const options = { guards, actions };

const galleryItemMachine = Machine(config, options);

export const generateGalleryItemMachine = (screenshot: ModelScreenshot) => {
  return galleryItemMachine.withContext({
    screenshot,
    isRenderingFailed: false,
    isRenderingComplete: false,
    isRenderingInProgress: false,
    isRenderingQueued: false,
  });
};

export default galleryItemMachine;
